import { ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { Message } from 'primeng/api';
import { FileSelectEvent, FileUpload, FileUploadErrorEvent, FileUploadEvent, FileUploadHandlerEvent } from 'primeng/fileupload';
import { Subject, takeUntil } from 'rxjs';
import { AppConstants } from 'src/app/shared/app.constants';
import { environment } from 'src/environments/environment';
import { EventStepperDataService } from './event-stepper-data.service';


@Component({
  selector: 'ness-event-attachments',
  templateUrl: './event-attachments.component.html'
})
export class EventAttachmentsComponent implements OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInput: FileUpload;
  attachmentUploadErrorMessage: string = null;
  displayAttachmentErrorMessage: boolean = false;
  eventForm: FormGroup = <FormGroup>this.controlContainer.form;
  attachmentsForm: FormGroup = this.fb.group({
    EventAttachmentID: '',
    EventID: '',
    AttachmentID: '',
    ENESS_PersonAttachments: this.fb.group({
      FileName: '',
      AttachmentID: '',
      CreatedBy: '',
      CreatedById: ''
    })
  });
  eventAttachmentsFormControl: FormControl = <FormControl>this.eventForm.get('ENESS_XREF_EventAttachments');
  readonly maxFileSize: number = environment.maxUploadFileSizeInBytes;
  readonly _maxFileLimitExceededMsg = "Total number of files exceed the max limit. No files were uploaded. ";
  readonly rows: number = AppConstants.defaultTableRows;
  readonly totalNumberOfUploadFilesAllowed: number = AppConstants.totalNumberOfUploadFilesAllowed;

  private destroyed$: Subject<void> = new Subject<void>();

  private _attachments: ServerModels.ENESS_XREF_EventAttachmentsDTO[] = [];
  public get attachments(): ServerModels.ENESS_XREF_EventAttachmentsDTO[] {
    this._attachments = this.eventAttachmentsFormControl.value || [];
    if (this.fileInput) {
      this._updateFileInputUploadedFilesCount();
    }
    return this._attachments;
  }

  public set attachments(attachments: ServerModels.ENESS_XREF_EventAttachmentsDTO[]) {
    this._attachments = attachments;
  }


  constructor(
    private controlContainer: FormGroupDirective,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private eventDataSvc: EventStepperDataService) { }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public deleteUploadedAttachment(idx: number): void {
    let attachmentId: string = this.attachments[idx].AttachmentStagingID;
    this.eventDataSvc.deleteEventAttachmentStaging(attachmentId).pipe(takeUntil(this.destroyed$)).subscribe(
      (result: number) => {
        if (result > -1) {
          this.attachments.splice(idx, 1);
          this.eventAttachmentsFormControl.setValue([...this.attachments]);
        }
        else {
          this.fileInput.msgs.push({ severity: 'error', summary: 'Error:', detail: 'An error occurred deleting event attachment.' });
        }
        this._updateFileInputUploadedFilesCount();
        this.fileInput.cd.markForCheck();
      }
    );
  }

  private _updateFileInputUploadedFilesCount(): void {
    this.fileInput.uploadedFileCount = this.eventAttachmentsFormControl?.value?.length ?? 0;
    this.fileInput.cd.markForCheck();
  }

  public onSelectFileHandler(evt: FileSelectEvent): void {
    this._updateFileInputUploadedFilesCount();
    this.fileInput.checkFileLimit(evt.files);
    if (this.fileInput.hasFiles() && !this.fileInput.isFileLimitExceeded()) {
      this.fileInput.upload();
    }
    this.fileInput.files = [];
    this.fileInput.clearInputElement();
    this.fileInput.cd.markForCheck();
  }

  public uploadEventAttachments(uploadEvent: FileUploadHandlerEvent): void {
    if (this.fileInput.fileLimit && this.fileInput.fileLimit >= this.fileInput.files.length + (this.eventAttachmentsFormControl?.value?.length ?? 0)) {
      let attachmentsForm = new FormData();
      attachmentsForm.append('EventID', this.eventDataSvc.eventId);
      attachmentsForm.append('EventStagingID', this.eventForm.get('EventStagingID').value);
      uploadEvent.files.forEach((file) => attachmentsForm.append('files[]', file));
      this.eventDataSvc.uploadEventAttachmentsStaging(attachmentsForm).pipe(takeUntil(this.destroyed$)).subscribe({
        next: (results: ServerModels.ENESS_XREF_EventAttachmentsDTO[]) => {
          this.eventAttachmentsFormControl.setValue([...this.attachments, ...results]);
          this._uploadComplete();
          this.fileInput.onUpload.emit(uploadEvent as FileUploadEvent);
          this.fileInput.cd.markForCheck();
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.fileInput.onError.emit({ files: this.fileInput.files, error: err });
        }
      });
    }
  }

  private _uploadComplete(): void {
    this._updateFileInputUploadedFilesCount();
    this.fileInput.uploading = false;
    this.fileInput.progress = 0;
    this.fileInput.clear();
  }

  public onUploadErrorHandler(errorEvent: FileUploadErrorEvent) {
    let messages: Message[] = [];
    if (this.fileInput.msgs) {
      this.fileInput.msgs.forEach(x => {
        if (x.summary.toLowerCase().indexOf('invalid file type') > -1) {
          messages.push(x);
        }
      });
    }
    this._uploadComplete();
    this.fileInput.msgs = [...messages];
    this.fileInput.msgs.push({ severity: 'error', summary: 'Error:', detail: errorEvent.error.error });
  }
}
